import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Building2, Landmark, Wrench, Server, Network, Code2, ShieldCheck } from "lucide-react";
import { MaskedLine, Reveal, Counter, Overline } from "@/components/motion";
import { CrestMark } from "@/components/Logo";
import EditorialMarquee from "@/components/EditorialMarquee";
import { IMAGES, DIVISIONS, STATS } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const crestX = useTransform(sx, (v) => v * 34);
  const crestY = useTransform(sy, (v) => v * 24);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden grain"
      onMouseMove={(e) => {
        mx.set(e.clientX / window.innerWidth - 0.5);
        my.set(e.clientY / window.innerHeight - 0.5);
      }}
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img src={IMAGES.heroInfra} alt="Monolithic concrete and steel architecture" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/70 to-[#0B0D12]/30" />
      <div className="absolute inset-0 hero-glow" />

      <motion.div
        className="absolute right-[6%] top-[16%] opacity-[0.16] hidden md:block"
        style={{ x: crestX, y: crestY }}
        initial={{ opacity: 0, rotate: -8 }}
        animate={{ opacity: 0.16, rotate: 0 }}
        transition={{ duration: 1.6, delay: 0.9 }}
      >
        <CrestMark size={420} />
      </motion.div>

      <motion.div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 pb-24 pt-44 w-full" style={{ opacity: fade }}>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="origin-left h-px w-24 bg-gradient-to-r from-[#3B82F6] to-transparent mb-8"
        />
        <MaskedLine delay={0.25}>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-sky-400/90" data-testid="hero-overline">
            Crestmont Enterprises Private Limited
          </span>
        </MaskedLine>
        <h1 className="mt-6 font-display font-semibold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          <MaskedLine delay={0.4}>Building</MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="gold-text">Opportunities</span>
          </MaskedLine>
          <MaskedLine delay={0.7}>for a Better</MaskedLine>
          <MaskedLine delay={0.85}>Tomorrow.</MaskedLine>
        </h1>
        <MaskedLine delay={1.0} className="mt-8 max-w-xl">
          <span className="text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="hero-subcopy">
            A multi-disciplinary institutional infrastructure giant — commanding strategic real estate development and
            Tier-1 mission-critical digital systems under one crest.
          </span>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.25 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/infrastructure"
            data-testid="hero-explore-divisions-btn"
            className="group flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:shadow-[0_0_32px_rgba(59,130,246,0.45)] transition-shadow duration-300"
          >
            Explore the Divisions
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-briefing-btn"
            className="text-sm font-medium text-slate-200 border border-[#282E3F] px-7 py-3.5 rounded-full hover:border-sky-400/60 hover:text-sky-300 transition-colors duration-300"
          >
            Request Institutional Briefing
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="mt-14 flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500"
        >
          <span>EST. THANE · MAHARASHTRA</span>
          <span className="w-8 h-px bg-[#282E3F]" />
          <span>NATIONAL OPERATIONS</span>
        </motion.div>
      </motion.div>
    </section>
  );
};

const StatsSection = () => (
  <section className="py-24 sm:py-32 relative" data-testid="stats-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-12">
      <Reveal>
        <Overline>Institutional Ethos</Overline>
        <h2 className="mt-4 font-display font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
          Two disciplines. One standard of permanence.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#282E3F]/70 rounded-xl overflow-hidden border border-[#282E3F]">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="bg-[#12151E] p-8">
            <Counter
              value={s.value}
              prefix={s.prefix}
              suffix={s.suffix}
              decimals={s.decimals}
              className="font-display font-semibold text-3xl sm:text-4xl gold-text tracking-tight"
            />
            <p className="mt-3 text-sm text-slate-400 leading-snug" data-testid={`stat-label-${i}`}>{s.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const DivisionCard = ({ division, delay }) => (
  <Reveal delay={delay}>
    <Link
      to={division.path}
      data-testid={`division-card-${division.number}`}
      className="group relative block h-[420px] sm:h-[520px] rounded-xl overflow-hidden border border-[#282E3F] hover:border-sky-500/50 transition-colors duration-500"
    >
      <img
        src={division.heroImage}
        alt={division.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/45 to-transparent" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_50%_100%,rgba(59,130,246,0.18),transparent_60%)]" />
      <div className="absolute inset-x-0 bottom-0 p-8 sm:p-10">
        <p className="font-mono text-xs tracking-[0.3em] text-sky-400/90">DIVISION {division.number}</p>
        <h3 className="mt-3 font-display font-semibold tracking-tight text-xl sm:text-2xl">{division.title}</h3>
        <p className="mt-2 text-sm text-slate-300 max-w-md">{division.tagline}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-sky-300">
          Enter Division
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

const DivisionsShowcase = () => (
  <section className="py-24 sm:py-32 border-t border-[#282E3F]/60" data-testid="divisions-showcase">
    <div className="max-w-7xl mx-auto px-6 sm:px-12">
      <Reveal className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <Overline>The Divisions</Overline>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Steel in the ground.<br />Intelligence in the wire.
          </h2>
        </div>
        <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
          Crestmont operates two institutional divisions, each held to a single mandate — systems and structures that outlast their mandate.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <DivisionCard division={DIVISIONS.infra} delay={0.05} />
        <DivisionCard division={DIVISIONS.tech} delay={0.15} />
      </div>
    </div>
  </section>
);

const BENTO = [
  { icon: Building2, title: "Large-Scale Construction & Development", desc: "Residential, commercial, and industrial projects executed with architectural precision.", span: "lg:col-span-4", img: IMAGES.infraFacade },
  { icon: Server, title: "Mission-Critical Server Maintenance", desc: "Tier-1 AMC for NSE, BSE, MCX ecosystems and HFT clusters.", span: "lg:col-span-2" },
  { icon: Landmark, title: "Institutional Asset Management", desc: "Strategic acquisition, leasing, and management of high-value immovable properties.", span: "lg:col-span-2" },
  { icon: Network, title: "System Integration & IT Consultancy", desc: "Integrated solutions for complex data processing environments.", span: "lg:col-span-4", img: IMAGES.techFiber },
  { icon: Wrench, title: "Turnkey Renovation & Commissioning", desc: "End-to-end renovation and project commissioning on a contract basis.", span: "lg:col-span-3" },
  { icon: Code2, title: "Enterprise Software Engineering", desc: "Development and customization of specialized applications and software programs.", span: "lg:col-span-3" },
];

const ServicesBento = () => (
  <section className="py-24 sm:py-32 border-t border-[#282E3F]/60" data-testid="services-bento">
    <div className="max-w-7xl mx-auto px-6 sm:px-12">
      <Reveal>
        <Overline>Core Capabilities</Overline>
        <h2 className="mt-4 font-display font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Six instruments of execution.</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-8">
        {BENTO.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.06} className={c.span}>
            <div
              data-testid={`bento-card-${i}`}
              className="card-institutional group h-full min-h-[220px] flex flex-col justify-end shadow-2xl hover:-translate-y-1 transition-transform duration-300"
            >
              {c.img && (
                <>
                  <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#181C27] via-[#181C27]/80 to-[#181C27]/30" />
                </>
              )}
              <div className="absolute top-0 left-0 h-px w-0 group-hover:w-full bg-gradient-to-r from-sky-400/70 via-blue-500/40 to-transparent transition-all duration-700" />
              <div className="relative">
                <c.icon size={26} className="text-sky-400/90" strokeWidth={1.6} />
                <h3 className="mt-5 font-display font-medium text-lg sm:text-xl tracking-tight">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Compliance = () => (
  <section className="py-24 sm:py-32 border-t border-[#282E3F]/60 relative overflow-hidden" data-testid="compliance-section">
    <img src={IMAGES.textureBronze} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.07]" />
    <div className="relative max-w-7xl mx-auto px-6 sm:px-12">
      <Reveal>
        <Overline>Technical Compliance</Overline>
        <h2 className="mt-4 font-display font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Held to institutional grade.</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <Reveal delay={0.05}>
          <div className="card-institutional h-full">
            <ShieldCheck size={26} className="text-emerald-400" strokeWidth={1.6} />
            <h3 className="mt-5 font-display font-medium text-lg sm:text-xl">IT Standards</h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Excellence in data processing and system integration for the financial ecosystems of NSE, BSE, and MCX — engineered for high-frequency, zero-failure environments.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["NSE", "BSE", "MCX", "HFT CLUSTERS", "TIER-1 AMC"].map((b) => (
                <span key={b} className="font-mono text-[10px] tracking-[0.2em] px-3 py-1.5 rounded-full border border-[#282E3F] text-slate-300 bg-[#0B0D12]/60">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="card-institutional h-full">
            <Landmark size={26} className="text-sky-400" strokeWidth={1.6} />
            <h3 className="mt-5 font-display font-medium text-lg sm:text-xl">Real Estate Standards</h3>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
              Full-spectrum project development and colonization — acquisition, execution, and commissioning governed by institutional discipline.
            </p>
            <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-slate-500 border-t border-[#282E3F] pt-5">
              SCOPE NOTE — FOCUSED ON BACKEND TECHNICAL SYSTEMS (EXCLUDING WEB-PAGE DESIGNING)
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="py-28 sm:py-36 border-t border-[#282E3F]/60 relative hero-glow" data-testid="home-cta">
    <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
      <Reveal>
        <CrestMark size={56} className="mx-auto" />
        <h2 className="mt-8 font-display font-semibold uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          Commission the <span className="gold-text">Crestmont Standard</span>
        </h2>
        <p className="mt-5 text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
          Engage our institutional desk for development mandates, asset stewardship, or mission-critical systems maintenance.
        </p>
        <Link
          to="/contact"
          data-testid="cta-contact-btn"
          className="group mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_36px_rgba(59,130,246,0.45)] transition-shadow duration-300"
        >
          Open an Institutional Enquiry
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <EditorialMarquee />
    <StatsSection />
    <DivisionsShowcase />
    <ServicesBento />
    <Compliance />
    <CTASection />
  </div>
);

export default Home;
